import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useTranslations } from 'use-intl';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { TaxAccountApiClient, type TaxHeldRowResolution } from '@/clients/taxAccountApiClient';

/** Mirrors the server's limit (`TAX_RESOLUTION_NOTE_MAX_LENGTH`). */
const NOTE_MAX_LENGTH = 1000;

const CHOICES = [
  { value: 'already_booked', key: 'alreadyBooked' },
  { value: 'paid_from_outside', key: 'paidFromOutside' },
] as const satisfies ReadonlyArray<{ value: TaxHeldRowResolution; key: string }>;

interface ResolveHeldTaxRowDialogProps {
  transactionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Reloads what the page shows, so the row appears resolved. */
  onResolved: () => void | Promise<void>;
}

/**
 * FIN-1617 D4/D5: a consultant resolves a deposit or payout held without a bank transaction, with
 * one of two choices and a mandatory note that is kept as the audit line. The system never picks
 * the counter account for "paid from outside": the row goes to Review (FIN-1207).
 *
 * Confirming is refused here, with a message, until a choice and a note exist; the server checks
 * both again. The button is natively disabled while the request runs.
 */
export const ResolveHeldTaxRowDialog: React.FC<ResolveHeldTaxRowDialogProps> = ({
  transactionId,
  open,
  onOpenChange,
  onResolved,
}) => {
  const t = useTranslations('consultantTax.resolveHeldRow');
  const [choice, setChoice] = useState<TaxHeldRowResolution | ''>('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);

  const resolve = useMutation({
    mutationFn: async (input: { resolution: TaxHeldRowResolution; note: string }) => {
      const outcome = await TaxAccountApiClient.resolveHeldRow(transactionId, input.resolution, input.note);
      // Inside the mutation, so "Saving…" holds until the page shows what the server now holds.
      await onResolved();
      return outcome;
    },
    onSuccess: (outcome) => {
      if (outcome.status === 'resolved') {
        onOpenChange(false);
        return;
      }
      setProblem(outcome.message);
    },
  });

  const confirm = () => {
    if (choice === '') {
      setProblem(t('chooseProblem'));
      return;
    }
    if (note.trim().length === 0) {
      setProblem(t('noteProblem'));
      return;
    }
    setProblem(null);
    resolve.mutate({ resolution: choice, note: note.trim() });
  };

  const handleOpenChange = (next: boolean) => {
    if (resolve.isPending) return;
    if (!next) {
      setChoice('');
      setNote('');
      setProblem(null);
      resolve.reset();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="tax-resolve-dialog">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <RadioGroup
          value={choice}
          onValueChange={(value) => {
            setChoice(value as TaxHeldRowResolution);
            setProblem(null);
          }}
          aria-label={t('choicesLabel')}
          data-testid="tax-resolve-choices"
        >
          {CHOICES.map((c) => (
            <div key={c.value} className="flex items-start gap-3">
              <RadioGroupItem value={c.value} id={`tax-resolve-${c.value}`} className="mt-1" />
              <Label htmlFor={`tax-resolve-${c.value}`} className="space-y-1 font-normal">
                <span className="block font-medium">{t(`choices.${c.key}.label`)}</span>
                <span className="block text-sm text-muted-foreground">{t(`choices.${c.key}.description`)}</span>
              </Label>
            </div>
          ))}
        </RadioGroup>

        <div className="space-y-2">
          <Label htmlFor="tax-resolve-note">{t('noteLabel')}</Label>
          <Textarea
            id="tax-resolve-note"
            value={note}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(event) => {
              setNote(event.target.value);
              setProblem(null);
            }}
            placeholder={t('notePlaceholder')}
            data-testid="tax-resolve-note"
          />
        </div>

        {problem && (
          <p className="text-sm text-destructive" role="alert" data-testid="tax-resolve-error">
            {problem}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={resolve.isPending}>
            {t('cancel')}
          </Button>
          <Button onClick={confirm} disabled={resolve.isPending} data-testid="tax-resolve-confirm">
            {resolve.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {resolve.isPending ? t('saving') : t('confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
